import React, { createContext, useContext, ReactNode } from "react";
import { setApiClient } from "../helpers/apiClient";

// Generic API client interface that matches the web app's APIClient
// Uses `any` at the boundary to accept the web app's specific types (AcceptedZodSchema, AxiosRequestConfig)
// without creating a hard dependency on those types.
export interface APIClientInterface {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  get: <R>(
    url: string,
    params?: any,
    timeout?: number,
    zodSchema?: any,
    config?: any
  ) => Promise<R | undefined>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  post: <R, D = any>(url: string, data: D, zodSchema?: any, config?: any) => Promise<R | undefined>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  put: <R, D = any>(url: string, data: D, zodSchema?: any) => Promise<R | undefined>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  delete: <R>(url: string, config?: any, zodSchema?: any) => Promise<R | undefined>;
  cancel: () => void;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface AppState extends Record<string, any> {
  rbacEnabled?: boolean;
}

export interface InternalContextValue {
  currentTenant: string;
  apiClient: APIClientInterface;
  appState: AppState;
}

const InternalContext = createContext<InternalContextValue | null>(null);

export interface InternalProviderProps {
  children: ReactNode;
  currentTenant: string;
  apiClient: APIClientInterface;
  appState: AppState;
}

export const InternalProvider: React.FC<InternalProviderProps> = ({
  children,
  currentTenant,
  apiClient,
  appState,
}) => {
  // Set the API client synchronously so it's available before React Query fires queries
  setApiClient(apiClient);

  return (
    <InternalContext.Provider value={{ currentTenant, apiClient, appState }}>
      {children}
    </InternalContext.Provider>
  );
};

export const useInternalContext = (): InternalContextValue => {
  const context = useContext(InternalContext);
  if (!context) {
    throw new Error("useInternalContext must be used within an InternalProvider");
  }
  return context;
};
